import type { AgeGroup } from '@/api/core-api/types.gen';

import { MAXIMUM_TARGETING_AGE, MINIMUM_TARGETING_AGE } from '@common/utils';

export const AGE_GROUP_OPTIONS: { id: AgeGroup; label: string }[] = [
  { id: '18-20', label: '18–20' },
  { id: '21-29', label: '21–29' },
  { id: '30-39', label: '30–39' },
  { id: '40-54', label: '40–54' },
  { id: '55+', label: '55+' },
];

export const MINIMUM_AGE = MINIMUM_TARGETING_AGE;
/** The highest age either bound can be saved as, an inclusive cutoff. */
export const MAXIMUM_AGE = MAXIMUM_TARGETING_AGE;
/**
 * The slider's last notch, one past `MAXIMUM_AGE` and labelled `75+`: no
 * upper limit. It is never saved as a number; `ageFieldsForRange` turns it
 * into a null `maxAge`.
 */
export const OPEN_ENDED_AGE = MAXIMUM_AGE + 1;

/**
 * Share of the `55+` census group older than `MAXIMUM_AGE`, a little under a
 * quarter in US and UK census data. Only an open-ended range recruits them.
 */
const OVER_MAXIMUM_AGE_SHARE = 0.23;

export type AgeRange = [number, number];

const GROUP_BOUNDS: Record<Exclude<AgeGroup, 'N/A'>, AgeRange> = {
  '18-20': [18, 20],
  '21-29': [21, 29],
  '30-39': [30, 39],
  '40-54': [40, 54],
  '55+': [55, OPEN_ENDED_AGE],
};

export function ageRangeFromProject({
  minAge,
  maxAge,
  ageGroups,
}: {
  minAge?: number | null;
  maxAge?: number | null;
  ageGroups?: AgeGroup[];
}): AgeRange {
  if (minAge != null || maxAge != null) {
    return [minAge ?? MINIMUM_AGE, maxAge ?? OPEN_ENDED_AGE];
  }

  const bounds = (ageGroups ?? [])
    .filter((group) => group !== 'N/A')
    .map((group) => GROUP_BOUNDS[group]);
  if (bounds.length === 0) return [MINIMUM_AGE, OPEN_ENDED_AGE];
  return [
    Math.min(...bounds.map(([min]) => min)),
    Math.max(...bounds.map(([, max]) => max)),
  ];
}

export function ageGroupsForRange([min, max]: AgeRange): AgeGroup[] {
  if (min === MINIMUM_AGE && max === OPEN_ENDED_AGE) return [];
  return AGE_GROUP_OPTIONS.filter(({ id }) => {
    if (id === 'N/A') return false;
    const [groupMin, groupMax] = GROUP_BOUNDS[id];
    return groupMax >= min && groupMin <= max;
  }).map(({ id }) => id);
}

/**
 * The part of a legacy age group that falls inside `range`, and the share of
 * the group's people that part covers, assuming ages spread evenly within a
 * group. `55+` runs to the open-ended notch: its over-75 share counts only
 * when the range is open-ended, and the years 55–75 split the rest.
 */
export function ageGroupOverlap(
  group: Exclude<AgeGroup, 'N/A'>,
  [min, max]: AgeRange,
): { range: AgeRange; coverage: number } | null {
  const [groupMin, groupMax] = GROUP_BOUNDS[group];
  const overlapMin = Math.max(groupMin, min);
  const overlapMax = Math.min(groupMax, max);
  if (overlapMin > overlapMax) return null;
  const range: AgeRange = [overlapMin, overlapMax];
  if (overlapMin === groupMin && overlapMax === groupMax) {
    return { range, coverage: 1 };
  }
  if (groupMax !== OPEN_ENDED_AGE) {
    return {
      range,
      coverage: (overlapMax - overlapMin + 1) / (groupMax - groupMin + 1),
    };
  }
  const years = Math.min(overlapMax, MAXIMUM_AGE) - overlapMin + 1;
  const overMaximum =
    overlapMax === OPEN_ENDED_AGE ? OVER_MAXIMUM_AGE_SHARE : 0;
  return {
    range,
    coverage:
      (years / (MAXIMUM_AGE - groupMin + 1)) * (1 - OVER_MAXIMUM_AGE_SHARE) +
      overMaximum,
  };
}

export function isAgeGroup(value: string): value is Exclude<AgeGroup, 'N/A'> {
  return Object.hasOwn(GROUP_BOUNDS, value);
}

/**
 * Keeps the minimum thumb at or below `MAXIMUM_AGE`, since the open-ended
 * notch is only ever an upper bound.
 */
export function clampAgeRange([min, max]: AgeRange): AgeRange {
  return [Math.min(min, MAXIMUM_AGE), max];
}

export function ageFieldsForRange([min, max]: AgeRange): {
  minAge: number | null;
  maxAge: number | null;
} {
  return {
    minAge: min === MINIMUM_AGE ? null : min,
    maxAge: max === OPEN_ENDED_AGE ? null : max,
  };
}

export function formatAgeRange([min, max]: AgeRange): string {
  if (min === MINIMUM_AGE && max === OPEN_ENDED_AGE) return 'All ages';
  if (max === OPEN_ENDED_AGE) return `${min}+`;
  if (min === MINIMUM_AGE) return `Up to ${max}`;
  return `${min}–${max}`;
}
